import "../../side.css";
import { Button, Nav } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import OKOCoreLogo from "../../../assets/OKO_Core_Logotype2.svg";
import { colors, constants } from "utils";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "hooks/useFirebase";
import { UserProfileCard } from "../../shared/UserProfileCard";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { ADMIN_NAV_GROUPS } from "./adminNavConfig";
import { useAdminNavOpenGroups } from "./useAdminNavOpenGroups";

const ICON_BOX_SIZE = "24px";
const iconBoxStyle = {
  alignItems: "center",
  display: "inline-flex",
  flexShrink: 0,
  height: ICON_BOX_SIZE,
  justifyContent: "center",
  width: ICON_BOX_SIZE
};

const NavItemGlyph = ({ icon, color }) => {
  if (!icon) return null;
  if (icon.faIcon) return <FontAwesomeIcon icon={icon.faIcon} style={{ color, fontSize: "20px" }} />;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox={icon.viewBox} fill="none" width="24" height="24">
      {icon.paths.map((d) => (
        <path key={d} fill={color} fillRule={icon.fillRule} clipRule={icon.fillRule} d={d} />
      ))}
    </svg>
  );
};

const NavItemIcon = ({ icon, color }) => (
  <span aria-hidden="true" style={iconBoxStyle}>
    <NavItemGlyph icon={icon} color={color} />
  </span>
);

const NavItem = ({ navItem, isActive }) => {
  const color = isActive ? colors.blue500 : colors.nightlyWoods900;
  return (
    <Nav.Item>
      <div className={"icon-nav-item" + (isActive ? "-active" : "")} style={{ paddingLeft: "36px" }}>
        <NavItemIcon icon={navItem.icon} color={color} />
        <Nav.Link as={Link} style={{ color, padding: 0 }} to={navItem.path}>
          {navItem.label}
        </Nav.Link>
      </div>
    </Nav.Item>
  );
};

const NavGroup = ({ group, isOpen, onToggle, isActive }) => {
  const listId = `admin-nav-group-${group.id}`;
  return (
    <div>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={onToggle}
        style={{
          alignItems: "center",
          background: "none",
          border: "none",
          color: colors.nightlyWoods900,
          display: "flex",
          fontSize: "16px",
          fontWeight: 600,
          justifyContent: "space-between",
          padding: "8px 12px",
          textAlign: "left",
          width: "100%"
        }}>
        {group.label}
        <span
          aria-hidden="true"
          style={{ fontSize: "10px", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
          ▶
        </span>
      </button>
      <div id={listId}>
        {isOpen && group.items.map((navItem) => (
          <NavItem key={navItem.path} navItem={navItem} isActive={isActive(navItem)} />
        ))}
      </div>
    </div>
  );
};

const navActionStyle = { color: colors.blue500, fontWeight: 600, padding: 0, textDecoration: "none" };

export const AdminSide = ({ showCloseButton, toggleSidebar, adminUserData = {} }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Use permission flags from backend /admin/me endpoint
  const canAccessClientAdmin = adminUserData?.can_access_client_admin || false;
  const canAccessUserAdmin = adminUserData?.can_access_user_admin || false;
  const canAccessSchoolAdmin = adminUserData?.can_access_school_admin || false;
  const canManageContent = adminUserData?.can_manage_content || false;

  const canAccessMenu = (menuKey) => {
    // Consensus and Crazy Calc. entries carry no menu key: every admin sees them
    if (!menuKey) return true;

    // Superuser-only entries share one predicate (no dedicated can_access_* flag)
    if (menuKey === "CLIENT_ADMIN") return canAccessClientAdmin;
    if (menuKey === "USER_ADMIN") return canAccessUserAdmin;
    if (menuKey === "EDNITION_ADMIN") return canAccessClientAdmin;
    if (menuKey === "FEATURE_FLAGS_ADMIN") return canAccessClientAdmin;
    if (menuKey === "GROUPING_SIMULATION") return canAccessClientAdmin;

    // Skills Profile - content managers; the other three entries also need superuser
    if (menuKey === "SKILLS_PROFILE_REVIEW") return canManageContent;
    if (menuKey === "SKILLS_PROFILE_ITEM_SELECTION") return canManageContent && canAccessClientAdmin;
    if (menuKey === "SKILLS_PROFILE_PROMPTS") return canManageContent && canAccessClientAdmin;
    if (menuKey === "SKILLS_PROFILE_LLM_TAGS") return canManageContent && canAccessClientAdmin;

    // Staff only, like its endpoints: a content manager who is not staff would get 403
    if (menuKey === "TEACHER_REPORTS_METRICS") return canAccessSchoolAdmin;
    if (menuKey === "WARM_UP_SETS_ADMIN") return canAccessSchoolAdmin;

    // Content menus and other admin features - superusers, oko_staff, or content managers
    return canAccessSchoolAdmin || canManageContent;
  };

  const isActive = (navItem) =>
    navItem.isActive ? navItem.isActive(location.pathname) : location.pathname === navItem.path;

  const visibleGroups = ADMIN_NAV_GROUPS
    .map((group) => ({ ...group, items: group.items.filter((navItem) => canAccessMenu(navItem.menuKey)) }))
    .filter((group) => group.items.length > 0);
  const activeGroupId = ADMIN_NAV_GROUPS.find((group) => group.items.some(isActive))?.id;
  const { openGroupIds, toggleGroup, expandAll, collapseAll } = useAdminNavOpenGroups(
    activeGroupId,
    visibleGroups.map((group) => group.id)
  );

  return (
    <div id="sidebar-toggle" style={{
      borderRight: `2px solid ${colors.nightlyWoods500}`,
      bottom: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: "100vh",
      overflow: "auto",
      paddingBottom: "32px",
      position: "fixed",
      top: 0,
      width: "312px"
    }}>
      {showCloseButton && <Button onClick={toggleSidebar}
        style={{
          backgroundColor: colors.nightlyWoods500,
          border: "none",
          position: "absolute",
          left: "262px",
          top: "8px"
        }}>
        <FontAwesomeIcon icon={faClose} />
      </Button>}
      <div>
        <div style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          padding: "40px 32px 16px 32px",
          height: "100px"
        }}>
          <img src={OKOCoreLogo} alt="logo" />
        </div>
        <div style={{ display: "flex", gap: "16px", padding: "24px 26px 0 26px" }}>
          <Button variant="link" size="sm" onClick={expandAll} style={navActionStyle}>
            {constants.ADMIN_NAV_EXPAND_ALL}
          </Button>
          <Button variant="link" size="sm" onClick={collapseAll} style={navActionStyle}>
            {constants.ADMIN_NAV_COLLAPSE_ALL}
          </Button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "16px 0 0 8px" }}>
          <Nav style={{ display: "block", gap: "4px", padding: "0 6px" }}>
            {visibleGroups.map((group) => (
              <NavGroup
                key={group.id}
                group={group}
                isOpen={openGroupIds.includes(group.id)}
                onToggle={() => toggleGroup(group.id)}
                isActive={isActive}
              />
            ))}
          </Nav>
        </div>
      </div>
      <div>
        <UserProfileCard user={user} />
      </div>
    </div>
  );
};
